import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { CreditCard, Mail, Smartphone, Truck, Check, ChevronRight, ChevronLeft, SkipForward, Send } from "lucide-react";

const STEPS = [
  { key: "welcome", title: "Welcome", icon: Check },
  { key: "square", title: "Square", icon: CreditCard },
  { key: "cashapp", title: "Cash App", icon: Send },
  { key: "email", title: "Email", icon: Mail },
  { key: "sms", title: "SMS", icon: Smartphone },
  { key: "shipping", title: "Shipping", icon: Truck },
  { key: "done", title: "Done", icon: Check },
];

export default function SetupWizard() {
  const { API, user, authHeaders } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    square_environment: "sandbox", square_application_id: "", square_access_token: "", square_location_id: "", cash_app_pay_enabled: true,
    cashapp_cashtag: "", cashapp_display_name: "",
    email_provider: "resend", email_api_key: "", email_from: "",
    sms_provider: "twilio", sms_account_sid: "", sms_auth_token: "", sms_from_number: "",
    shipping_mode: "pickup_only", shipping_flat_rate: "", shipping_api_key: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== "owner") nav("/admin");
  }, [user, nav]);

  if (!user) return <div className="p-20 text-center"><Link to="/login" className="btn-primary">SIGN IN</Link></div>;
  if (user.role !== "owner") return <div className="p-20 text-center font-mono">Owner only.</div>;

  const saveStep = async (partial) => {
    setSaving(true);
    try {
      const body = {};
      Object.entries(partial).forEach(([k, v]) => { if (v !== "" && v != null) body[k] = v; });
      if (body.shipping_flat_rate) body.shipping_flat_rate = parseFloat(body.shipping_flat_rate);
      if (Object.keys(body).length > 0) {
        await axios.put(`${API}/admin/settings`, body, { headers: authHeaders() });
      }
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const next = async (partial) => { if (partial) await saveStep(partial); setStep(s => s + 1); };
  const back = () => setStep(s => Math.max(0, s - 1));
  const skip = () => setStep(s => s + 1);

  const finish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/complete-onboarding`, {}, { headers: authHeaders() });
      toast.success("Setup complete! Welcome aboard.");
      nav("/admin");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const current = STEPS[step];

  return (
    <div className="min-h-screen bg-[var(--bg)] py-8 px-4" data-testid="setup-wizard">
      <div className="max-w-2xl mx-auto">
        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-8" data-testid="wizard-progress">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`flex-1 h-2 border-2 border-[var(--ink)] ${i <= step ? 'bg-[var(--accent)]' : 'bg-[var(--paper)]'}`}/>
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center border-2 border-[var(--ink)]">
            <current.icon size={22}/>
          </div>
          <div>
            <div className="font-mono text-xs text-[var(--accent)]">STEP {step+1} OF {STEPS.length}</div>
            <h1 className="font-display text-3xl">{current.title.toUpperCase()}</h1>
          </div>
        </div>

        <div className="card-industrial p-6 mb-6 min-h-[320px]">
          {step === 0 && (
            <div data-testid="step-welcome">
              <h2 className="font-display text-2xl mb-3">LET'S GET YOU SET UP</h2>
              <p className="text-[var(--ink-2)] mb-4">We'll walk through 4 quick integrations to power your store:</p>
              <ul className="space-y-3 font-mono text-sm">
                <li className="flex items-center gap-2"><CreditCard size={16}/> <strong>Square + Cash App Pay</strong> — accept card and Cash App payments online</li>
                <li className="flex items-center gap-2"><Send size={16}/> <strong>Cash App $cashtag</strong> — manual Cash App payments to your $tag</li>
                <li className="flex items-center gap-2"><Mail size={16}/> <strong>Email</strong> — order confirmations & receipts</li>
                <li className="flex items-center gap-2"><Smartphone size={16}/> <strong>SMS</strong> — "your order is ready" texts</li>
                <li className="flex items-center gap-2"><Truck size={16}/> <strong>Shipping</strong> — flat rate or pickup-only</li>
              </ul>
              <p className="font-mono text-xs text-[var(--ink-2)] mt-4">You can skip any step and configure it later from Dashboard → Settings.</p>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-square">
              <h2 className="font-display text-2xl mb-2">CONNECT SQUARE</h2>
              <p className="text-[var(--ink-2)] text-sm mb-4">Get your credentials from <a href="https://developer.squareup.com/apps" target="_blank" rel="noreferrer" className="underline">Square Developer Dashboard → your app → Credentials & Locations</a>. Use Sandbox for testing, Production when you're ready to take real money.</p>
              <label className="font-mono text-xs block mb-1">ENVIRONMENT</label>
              <select value={data.square_environment} onChange={e=>setData({...data, square_environment: e.target.value})} className="input-field mb-3" data-testid="square-env-select">
                <option value="sandbox">Sandbox (testing)</option>
                <option value="production">Production (live)</option>
              </select>
              <label className="font-mono text-xs block mb-1">APPLICATION ID</label>
              <input type="text" value={data.square_application_id} onChange={e=>setData({...data, square_application_id: e.target.value})} placeholder="sandbox-sq0idb-... or sq0idp-..." className="input-field font-mono text-xs mb-3" data-testid="square-app-id-input"/>
              <label className="font-mono text-xs block mb-1">ACCESS TOKEN</label>
              <input type="password" value={data.square_access_token} onChange={e=>setData({...data, square_access_token: e.target.value})} placeholder="EAAAEx... (full token)" className="input-field font-mono text-xs mb-3" data-testid="square-token-input"/>
              <label className="font-mono text-xs block mb-1">LOCATION ID</label>
              <input type="text" value={data.square_location_id} onChange={e=>setData({...data, square_location_id: e.target.value})} placeholder="L..." className="input-field font-mono text-xs mb-3" data-testid="square-location-input"/>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!data.cash_app_pay_enabled} onChange={e=>setData({...data, cash_app_pay_enabled: e.target.checked})} data-testid="cash-app-toggle"/>
                <span className="font-mono text-xs">Also enable <strong>Cash App Pay</strong> at checkout (US customers only)</span>
              </label>
              <p className="font-mono text-xs text-[var(--ink-2)] mt-3">Your access token is private. Only owners and platform admins can see it.</p>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-cashapp">
              <h2 className="font-display text-2xl mb-2">CASH APP $CASHTAG</h2>
              <p className="text-[var(--ink-2)] text-sm mb-4">A simpler alternative to Square — customers tap a button at checkout and are sent to <code>cash.app/$yourtag</code> with the amount filled in. You verify each payment in your own Cash App and mark the order paid in the dashboard.</p>
              <label className="font-mono text-xs block mb-1">YOUR $CASHTAG</label>
              <input type="text" value={data.cashapp_cashtag} onChange={e=>setData({...data, cashapp_cashtag: e.target.value})} placeholder="$WayneWhiteSr" className="input-field font-mono text-sm mb-3" data-testid="cashapp-tag-wizard"/>
              <label className="font-mono text-xs block mb-1">DISPLAY NAME (OPTIONAL)</label>
              <input type="text" value={data.cashapp_display_name} onChange={e=>setData({...data, cashapp_display_name: e.target.value})} placeholder="Wayne White Sr." className="input-field" data-testid="cashapp-display-wizard"/>
              <p className="font-mono text-xs text-[var(--ink-2)] mt-3">Skip this if you only want Square's auto-verified Cash App Pay button.</p>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-email">
              <h2 className="font-display text-2xl mb-2">EMAIL PROVIDER</h2>
              <p className="text-[var(--ink-2)] text-sm mb-5">Send order confirmations & status updates to customers.</p>
              <label className="font-mono text-xs block mb-1">PROVIDER</label>
              <select value={data.email_provider} onChange={e=>setData({...data, email_provider: e.target.value})} className="input-field mb-3" data-testid="email-provider-select">
                <option value="resend">Resend (recommended — 5K/month free)</option>
                <option value="sendgrid">SendGrid (100/day free)</option>
              </select>
              <label className="font-mono text-xs block mb-1">API KEY</label>
              <input type="password" value={data.email_api_key} onChange={e=>setData({...data, email_api_key: e.target.value})} placeholder={data.email_provider === "resend" ? "re_..." : "SG..."} className="input-field font-mono text-xs mb-3" data-testid="email-key-input"/>
              <label className="font-mono text-xs block mb-1">FROM ADDRESS</label>
              <input type="email" value={data.email_from} onChange={e=>setData({...data, email_from: e.target.value})} placeholder="orders@lecroystore.com" className="input-field" data-testid="email-from-input"/>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-sms">
              <h2 className="font-display text-2xl mb-2">SMS (TWILIO)</h2>
              <p className="text-[var(--ink-2)] text-sm mb-5">Text customers when their order is ready for pickup. Sign up at <a href="https://twilio.com" target="_blank" rel="noreferrer" className="underline">twilio.com</a>.</p>
              <label className="font-mono text-xs block mb-1">ACCOUNT SID</label>
              <input type="password" value={data.sms_account_sid} onChange={e=>setData({...data, sms_account_sid: e.target.value})} placeholder="AC..." className="input-field font-mono text-xs mb-3" data-testid="sms-sid-input"/>
              <label className="font-mono text-xs block mb-1">AUTH TOKEN</label>
              <input type="password" value={data.sms_auth_token} onChange={e=>setData({...data, sms_auth_token: e.target.value})} className="input-field font-mono text-xs mb-3" data-testid="sms-token-input"/>
              <label className="font-mono text-xs block mb-1">SENDING PHONE NUMBER</label>
              <input value={data.sms_from_number} onChange={e=>setData({...data, sms_from_number: e.target.value})} placeholder="+14236457402" className="input-field" data-testid="sms-phone-input"/>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-shipping">
              <h2 className="font-display text-2xl mb-2">SHIPPING</h2>
              <p className="text-[var(--ink-2)] text-sm mb-5">How will orders reach customers?</p>
              <label className="font-mono text-xs block mb-2">MODE</label>
              <div className="space-y-2 mb-4">
                {[
                  { v: "pickup_only", label: "Pickup Only", sub: "Customers pick up at the store address (current default)" },
                  { v: "flat_rate", label: "Flat Rate Shipping", sub: "Charge a single $X per order for delivery" },
                  { v: "shippo", label: "Shippo API (live rates)", sub: "USPS/UPS/FedEx real rates + label printing — paste API key below" },
                ].map(o => (
                  <label key={o.v} className={`flex items-start gap-3 p-3 border-2 cursor-pointer ${data.shipping_mode===o.v?'border-[var(--accent)] bg-[var(--muted)]':'border-[var(--ink)]'}`} data-testid={`ship-${o.v}`}>
                    <input type="radio" checked={data.shipping_mode===o.v} onChange={()=>setData({...data, shipping_mode: o.v})} className="mt-1"/>
                    <div><div className="font-display">{o.label}</div><div className="font-mono text-xs text-[var(--ink-2)]">{o.sub}</div></div>
                  </label>
                ))}
              </div>
              {data.shipping_mode === "flat_rate" && (
                <div>
                  <label className="font-mono text-xs block mb-1">FLAT RATE ($)</label>
                  <input type="number" step="0.01" value={data.shipping_flat_rate} onChange={e=>setData({...data, shipping_flat_rate: e.target.value})} placeholder="9.99" className="input-field" data-testid="ship-rate-input"/>
                </div>
              )}
              {data.shipping_mode === "shippo" && (
                <div>
                  <label className="font-mono text-xs block mb-1">SHIPPO API TOKEN</label>
                  <input type="password" value={data.shipping_api_key} onChange={e=>setData({...data, shipping_api_key: e.target.value})} placeholder="shippo_live_..." className="input-field font-mono text-xs" data-testid="ship-key-input"/>
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div data-testid="step-done" className="text-center py-8">
              <div className="w-20 h-20 bg-[var(--accent)] text-white border-2 border-[var(--ink)] mx-auto mb-4 flex items-center justify-center"><Check size={40}/></div>
              <h2 className="font-display text-3xl mb-2">YOU'RE ALL SET</h2>
              <p className="text-[var(--ink-2)] mb-4">Your store is ready to take orders. You can update any integration anytime from <strong>Dashboard → Settings</strong>.</p>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="flex gap-3" data-testid="wizard-nav">
          {step > 0 && step < 6 && <button onClick={back} disabled={saving} className="btn-outline inline-flex items-center gap-2" data-testid="wiz-back"><ChevronLeft size={16}/> BACK</button>}
          {step > 0 && step < 6 && <button onClick={skip} disabled={saving} className="btn-outline inline-flex items-center gap-2" data-testid="wiz-skip"><SkipForward size={14}/> SKIP</button>}
          <div className="flex-1"/>
          {step === 0 && <button onClick={()=>setStep(1)} className="btn-primary inline-flex items-center gap-2" data-testid="wiz-start">GET STARTED <ChevronRight size={16}/></button>}
          {step >= 1 && step <= 5 && <button onClick={()=>next(data)} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="wiz-next">{saving?"SAVING...":"SAVE & NEXT"} <ChevronRight size={16}/></button>}
          {step === 6 && <button onClick={finish} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="wiz-finish">{saving?"FINISHING...":"GO TO DASHBOARD"} <ChevronRight size={16}/></button>}
        </div>
      </div>
    </div>
  );
}
